import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './context/ToastContext';
import { AuthProvider } from './context/AuthContext';

import { Zap } from 'lucide-react';
import HomeDashboard from './pages/Dashboard';
import { getValidToken } from './utils/token';

// Lazy load components for performance
const Login = React.lazy(() => import('./pages/Login'));
const Layout = React.lazy(() => import('./pages/Layout'));
const Invoices = React.lazy(() => import('./pages/Invoices'));
const Reports = React.lazy(() => import('./pages/Reports'));
const Roles = React.lazy(() => import('./pages/settings/Roles'));
const Permissions = React.lazy(() => import('./pages/settings/Permissions'));
const Users = React.lazy(() => import('./pages/settings/Users'));
const QBDSetup = React.lazy(() => import('./pages/settings/QBDSetup'));
const Vendors = React.lazy(() => import('./pages/Vendors'));
const Profile = React.lazy(() => import('./pages/Profile'));
const SessionTimeout = React.lazy(() => import('./components/common/SessionTimeout'));

const LoadingFallback = () => (
  <div className="fixed inset-0 bg-slate-50/50 backdrop-blur-sm flex flex-col items-center justify-center z-[100] animate-in fade-in duration-500">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-rose-100 rounded-2xl animate-spin" />
      <div className="absolute inset-0 flex items-center justify-center">
        <Zap className="w-6 h-6 text-[#01A5D6] animate-pulse" />
      </div>
    </div>
    <p className="mt-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] animate-pulse">Synchronizing Architecture</p>
  </div>
);


const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Validate the token's signature-independent claims (presence + expiry), not
  // just that a string exists — an expired/tampered token no longer renders the
  // authenticated shell. The server remains the real authority on every request.
  const token = getValidToken();
  return token ? <>{children}</> : <Navigate to="/login?expired=true" replace />;
};

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Router>
          <React.Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />

              {/* Protected Main App with Sidebar */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <SessionTimeout>
                      <Layout />
                    </SessionTimeout>
                  </ProtectedRoute>
                }
              >
                <Route index element={<HomeDashboard />} />
                <Route path="dashboard" element={<HomeDashboard />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="reports" element={<Reports />} />
                <Route path="vendors" element={<Vendors />} />
                <Route path="profile" element={<Profile />} />

                {/* Settings Sub-section */}
                <Route path="settings">
                  <Route index element={<Navigate to="roles" replace />} />
                  <Route path="roles" element={<Roles />} />
                  <Route path="permissions" element={<Permissions />} />
                  <Route path="users" element={<Users />} />
                  <Route path="qbd" element={<QBDSetup />} />
                </Route>

                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Routes>
          </React.Suspense>
        </Router>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;